import React from 'react';

// Presentational membership card shared by the /membership/plans grid and the
// signup onboarding picker. `features` is an array of { label, included }.
// When `onSelect` is provided the whole card acts as a selectable button.
export default function MembershipCard({
    tier,
    features = [],
    selected = false,
    highlighted = false,
    badgeText,
    onSelect,
    isDarkMode,
    children,
}) {
    const price = Number(tier?.Price || 0);
    const suffix = tier?.BillingFrequency === 'yearly' ? '/yr' : '/mo';
    const isFreeForever = price === 0 && !tier?.TrialDays;

    const subLabel = isFreeForever
        ? 'Basic access, no cost'
        : tier?.TrialDays
            ? `Free for ${tier.TrialDays} days, then billed`
            : 'Billed today';

    const borderCls = selected
        ? 'border-primary-400 shadow-[0_0_0_3px_rgba(52,211,153,0.35)]'
        : highlighted
            ? 'border-primary-500 shadow-xl'
            : isDarkMode
                ? 'border-slate-700 shadow-sm'
                : 'border-slate-200 shadow-sm';

    const Wrapper = onSelect ? 'button' : 'div';
    const wrapperProps = onSelect
        ? { type: 'button', onClick: onSelect, 'aria-pressed': selected }
        : {};

    return (
        <Wrapper
            {...wrapperProps}
            className={`text-left w-full rounded-3xl border flex flex-col relative transition-all duration-200 overflow-hidden ${borderCls} ${isDarkMode ? 'bg-slate-800' : 'bg-white'} ${onSelect ? 'cursor-pointer hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-primary-400' : ''}`}
        >
            {/* Top banner */}
            <div className="px-6 pt-7 pb-5 bg-slate-900 relative">
                <div className="absolute top-4 right-4 flex flex-col items-end gap-1">
                    {selected && (
                        <span className="bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
                            ✓ Selected
                        </span>
                    )}
                    {!selected && badgeText && (
                        <span className="bg-primary-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow">
                            {badgeText}
                        </span>
                    )}
                </div>
                <p className="text-slate-400 text-xs font-bold uppercase tracking-widest mb-1">Plan</p>
                <h2 className="text-2xl font-bold text-white pr-24">{tier?.TierName}</h2>
            </div>

            {/* Body */}
            <div className="flex-1 flex flex-col p-6 gap-4">
                <div className="text-center">
                    <div className="flex items-baseline justify-center gap-1">
                        <span className={`text-4xl font-extrabold ${isDarkMode ? 'text-primary-400' : 'text-primary-600'}`}>
                            ${price.toFixed(2)}
                        </span>
                        <span className={`text-sm font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{suffix}</span>
                    </div>
                    <p className={`mt-1 text-xs font-semibold ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{subLabel}</p>
                    {tier?.TierDescription && (
                        <p className={`mt-2 text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{tier.TierDescription}</p>
                    )}
                </div>

                <div className={`border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`} />

                <ul className="space-y-2 flex-1">
                    {features.map((f, i) => (
                        <li
                            key={`${tier?.TierID}-feature-${i}`}
                            className={`flex items-center gap-2 text-sm ${f.included ? (isDarkMode ? 'text-slate-200' : 'text-slate-700') : (isDarkMode ? 'text-slate-600' : 'text-slate-400')}`}
                        >
                            <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${f.included ? 'bg-primary-100 text-primary-600' : 'bg-slate-100 text-slate-400'}`}>
                                {f.included ? '✓' : '✗'}
                            </span>
                            <span className={f.included ? '' : 'line-through'}>{f.label}</span>
                            {f.included && f.badge && (
                                <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${isDarkMode ? 'bg-primary-900/50 text-primary-300' : 'bg-primary-100 text-primary-600'}`}>
                                    {f.badge}
                                </span>
                            )}
                        </li>
                    ))}
                </ul>

                {children}
            </div>
        </Wrapper>
    );
}
